import { Component, forwardRef } from '@angular/core';
import {
  AbstractControl, ControlValueAccessor, UntypedFormBuilder, UntypedFormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR,
  ValidationErrors, Validator, Validators
} from '@angular/forms';
import { takeUntil } from 'rxjs/operators';

import { EmailType, FieldStatus } from 'generated/graphql';

import { FormGroupConfig } from 'core/form-config-type';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import * as CustomValidators from 'core/validators';

export interface EmailFormModel {
  // Model of form value
  email: string;
  emailType: EmailType | 'home' | 'work';
  verified: boolean;
  subscribed: boolean;
}

@Component({
  selector: 'app-email-form',
  templateUrl: './email-form.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EmailFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => EmailFormComponent),
      multi: true
    }
  ]
})
export class EmailFormComponent
  extends WithDisposableSubject()
  implements ControlValueAccessor, Validator {
  formGroup: UntypedFormGroup;

  static toFormModel(email: { email: string; status: any; emailType: any; subscribed: boolean }): EmailFormModel {
    if (!email) {
      return null;
    }
    return {
      email: email.email,
      verified: email.status === FieldStatus.Verified,
      emailType: email.emailType,
      subscribed: email.subscribed
    };
  }

  private _onTouched = () => { };

  constructor(private _fb: UntypedFormBuilder) {
    super();
    const config: FormGroupConfig<EmailFormModel> = {
      emailType: ['home', [Validators.required]],
      email: [null, [CustomValidators.emailRequired]],
      verified: [false],
      subscribed: [false]
    };

    this.formGroup = this._fb.group(config);
  }

  /** Control Value Accessor Implementmenation */
  writeValue(obj: EmailFormModel): void {
    if (obj) {
      this.formGroup.setValue(obj, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.formGroup.valueChanges.pipe(
      takeUntil(this.disposer)
    ).subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }
  }

  /** Validator Implementation */
  validate(control: AbstractControl): ValidationErrors {
    if (this.formGroup.valid) {
      return null;
    }
    return { email: true };
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.formGroup.valueChanges.pipe(
      takeUntil(this.disposer)
    ).subscribe(fn);
  }
}
